import { useSelector } from 'react-redux'
import {
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon,
  AlignTopIcon,
  BottomIcon,
  AlignMiddleIcon,
  TextIcon,
} from '@gocanvas/gcds/icons'
import { 
  validateDimensionChanges,
  validatePositionChanges,
} from 'apps/studio/lib/utils/position'
import { SettingsLabel } from 'apps/studio/components/shared/inputs/SettingsLabel'
import { activeStyledElementSelector } from 'apps/studio/store/selectors/pdf'

import {
  MIN_RESIZE_WIDTH,
  MIN_RESIZE_HEIGHT,
} from 'apps/studio/lib/utils/dnd'

// Typography
export const FontSelect = {
  label: 'Family',
  settingType: 'elementFontFamilySelect',
  property: 'fontFamily',
  updateType: 'elementStyles',
}

export const FontColor = {
  label: 'Color',
  settingType: 'colorInput',
  property: 'fontColor',
  updateType: 'elementStyles',
}

export const FontStyleSelect = {
  label: 'Style',
  settingType: 'fontStyleSelect',
  property: 'fontStyle',
  updateType: 'elementStyles',
  choices: [
    {
      label: 'Regular',
      value: 'normal',
      additionalProperties: {
        fontWeight: 400,
        textDecoration: 'none',
      },
    },
    {
      label: 'Italic',
      value: 'italic',
      additionalProperties: {
        fontWeight: 400,
        textDecoration: 'none',
      },
      style: {
        fontStyle: 'italic',
      }
    },
    {
      label: 'Bold',
      value: 'normal',
      additionalProperties: {
        fontWeight: 700,
        textDecoration: 'none',
      },
      style: {
        fontWeight: 700,
      },
    },
    {
      label: 'Bold Italic',
      value: 'italic',
      additionalProperties: {
        fontWeight: 700,
        textDecoration: 'none',
      },
      style: {
        fontWeight: 700,
        fontStyle: 'italic',
      },
    },
    {
      label: 'Underline',
      value: 'normal',
      additionalProperties: {
        fontWeight: 400,
        textDecoration: 'underline',
      },
      style: {
        fontWeight: 400,
        textDecoration: 'underline',
      },
    },
    {
      label: 'Underline Italic',
      value: 'italic',
      additionalProperties: {
        fontWeight: 400,
        textDecoration: 'underline',
      },
      style: {
        fontStyle: 'italic',
        textDecoration: 'underline',
      },
    },
    {
      label: 'Bold Underline',
      value: 'normal',
      additionalProperties: {
        fontWeight: 700,
        textDecoration: 'underline',
      },
      style: {
        fontWeight: 700,
        textDecoration: 'underline',
      },
    },
    {
      label: 'Bold Underline Italic',
      value: 'italic',
      additionalProperties: {
        fontWeight: 700,
        textDecoration: 'underline',
      },
      style: {
        fontWeight: 700,
        fontStyle: 'italic',
        textDecoration: 'underline',
      },
    },
  ],
}

export const FontSize = {
  label: 'Size',
  settingType: 'numberInput',
  property: 'fontSize',
  updateType: 'elementStyles',
  min: 0,
}

// Typography :: Alignment
export const VerticalAlignment = {
  label: 'Vertical',
  settingType: 'elementStyleSelect',
  property: 'verticalAlignment',
  updateType: 'elementStyles',
  choices: [
    { label: 'Top', value: 'top', IconComponent: AlignTopIcon },
    { label: 'Middle', value: 'center', IconComponent: AlignMiddleIcon },
    { label: 'Bottom', value: 'bottom', IconComponent: BottomIcon },
  ],
}

export const HorizontalAlignment = {
  label: 'Horizontal',
  settingType: 'elementStyleSelect',
  property: 'horizontalAlignment',
  updateType: 'elementStyles',
  choices: [
    { label: 'Left', value: 'left', IconComponent: AlignLeftIcon },
    { label: 'Center', value: 'center', IconComponent: AlignCenterIcon },
    { label: 'Right', value: 'right', IconComponent: AlignRightIcon },
  ],
}


// Field Design
export const BackgroundColor = {
  label: 'Background',
  settingType: 'colorInput',
  property: 'backgroundColor',
  controlLabel: 'Field Background',
  updateType: 'elementStyles',
}

export const Dimensions = {
  label: 'Dimensions',
  settingType: 'positionGroup',
  updateType: 'elementPosition',
  inputs: [
    {
      label: 'W',
      property: 'width',
      validation: validateDimensionChanges,
      additionalProps: data => {
        const {
          x,
          page: { originalWidth },
        } = data
        return {
          step: 1,
          min: MIN_RESIZE_WIDTH,
          max: originalWidth - x,
        }
      },
    },
    {
      label: 'H',
      property: 'height',
      validation: validateDimensionChanges,
      additionalProps: data => {
        const {
          y,
          page: { originalHeight },
        } = data
        return {
          step: 1,
          min: MIN_RESIZE_HEIGHT,
          max: originalHeight - y,
        }
      },
    },
  ],
  showInput: () => {
    const activeElement = useSelector(activeStyledElementSelector)
    return activeElement
  },
}

export const Position = {
  label: 'Position',
  settingType: 'positionGroup',
  updateType: 'elementPosition',
  inputs: [
    {
      label: 'X',
      property: 'x',
      validation: validatePositionChanges,
      additionalProps: data => {
        const {
          page: { originalWidth },
          width,
        } = data
        return {
          step: 1,
          min: 0,
          max: originalWidth - width,
        }
      },
    },
    {
      label: 'Y',
      property: 'y',
      validation: validatePositionChanges,
      additionalProps: data => {
        const {
          page: { originalHeight },
          height,
        } = data
        return {
          step: 1,
          min: 0,
          max: originalHeight - height,
        }
      },
    },
  ],
  showInput: () => {
    const activeElement = useSelector(activeStyledElementSelector)
    return activeElement
  },
}

export const designSettings = [
  SettingsLabel('Font'),
  FontSelect,
  FontSize,
  FontStyleSelect,
  FontColor,
  SettingsLabel('Alignment'),
  HorizontalAlignment,
  VerticalAlignment,
  // VerticalSpacing,
  // Show Label
  // BackgroundColor,
  // SettingsLabel('Field'),
  // Dimensions,
  // Position,
  // Border
  // Border Location
]

export const DESIGN_SETTINGS_PANEL = 'Typography'

export const DesignSettingsPanel = {
  label: DESIGN_SETTINGS_PANEL,
  icon: TextIcon,
  settings: designSettings,
}
